/**
 * Алгоритм честности v2 — браузерная половина розыгрыша с участием людей.
 *
 * Порт api/internal/fair/v2.go. Отличие от v1 одно: в сид розыгрыша входит
 * выбор самих участников — цифра, которую каждый выбирает при входе. Цифры
 * попадают в лист Меркла вместе с номерами билетов, то есть фиксируются до
 * того, как станет известен внешний публичный сид.
 *
 * Что это даёт именно здесь, в браузере: человек может сложить цифры из
 * открытого списка участников, получить тот же множитель и пересчитать
 * победителей — не доверяя ни бренду, ни ALLSWIPES, ни этому коду. Поэтому
 * реализация обязана совпадать с серверной байт в байт; за этим следят общие
 * векторы в tests/fair-v1-vectors.json.
 */
import { bytesToHex, concat, encodeContext, hexToBytes, uint32BE, utf8 } from './bytes';
import {
  DRBG,
  SERVER_SEED_BYTES,
  TICKET_LEAF_LABEL,
  leafHash,
  merkleRoot,
  type DrawInput,
} from './v1';

export const ALGO_V2 = 'v2';
export const LABEL_DRAW_V2 = 'prizma.draw.v2';

/** Границы цифры участника. Ноль означает «цифра не выбрана». */
export const PICK_MIN = 1;
export const PICK_MAX = 9;

export interface DrawInputV2 extends DrawInput {
  /** Сумма цифр всех участников — складывается по открытому списку. */
  pickSum: number;
}

/**
 * Множитель участников: 1…9.
 *
 * Выводится только из суммы цифр, то есть только из выбора людей. Это то
 * число, которое отчёт показывает рядом с «что именно привело к такому
 * результату».
 */
export function multiplierV2(pickSum: number): number {
  if (pickSum <= 0) return 1;
  return 1 + (pickSum % 9);
}

/** Канонический транскрипт, к которому привязан DRBG. */
export function drawContextV2(input: DrawInputV2): Uint8Array {
  return encodeContext(
    utf8(input.campaignId),
    utf8(input.externalSeed),
    hexToBytes(input.ticketRoot),
    uint32BE(input.ticketCount),
    uint32BE(input.winnerCount),
    uint32BE(input.pickSum),
    uint32BE(multiplierV2(input.pickSum)),
  );
}

/** Лист списка билетов второй версии: номер и выбранная цифра. */
export function ticketLeafDataV2(number: string, pick: number): Uint8Array {
  return concat(utf8(TICKET_LEAF_LABEL), new Uint8Array([0x00]), utf8(`${number}:${pick}`));
}

export async function ticketLeavesV2(numbers: string[], picks: number[]): Promise<Uint8Array[]> {
  return Promise.all(numbers.map((n, i) => leafHash(ticketLeafDataV2(n, picks[i] ?? 0))));
}

/** Корень замороженного списка билетов вместе с цифрами участников. */
export async function ticketRootV2(numbers: string[], picks: number[]): Promise<string> {
  return bytesToHex(await merkleRoot(await ticketLeavesV2(numbers, picks)));
}

/**
 * Пересчёт победителей второй версии.
 *
 * Выборка та же, что в v1 — частичный Фишер — Йетс без смещения по модулю, —
 * меняется только то, чем засеян DRBG.
 */
export async function drawV2(serverSeed: Uint8Array, input: DrawInputV2): Promise<number[]> {
  if (serverSeed.length !== SERVER_SEED_BYTES) {
    throw new Error(`сид сервера должен быть ${SERVER_SEED_BYTES} байт, получено ${serverSeed.length}`);
  }
  if (input.winnerCount <= 0) return [];
  if (input.ticketCount < input.winnerCount) {
    throw new Error(`билетов (${input.ticketCount}) меньше, чем победителей (${input.winnerCount})`);
  }

  const drbg = new DRBG(serverSeed, LABEL_DRAW_V2, drawContextV2(input));
  const swapped = new Map<number, number>();
  const at = (i: number) => swapped.get(i) ?? i;

  const winners: number[] = [];
  for (let i = 0; i < input.winnerCount; i++) {
    const j = i + Number(await drbg.intN(BigInt(input.ticketCount - i)));
    const vi = at(i);
    const vj = at(j);
    winners.push(vj + 1);
    swapped.set(i, vj);
    swapped.set(j, vi);
  }
  return winners;
}

/** Сумма цифр участников по открытому списку. */
export function pickSum(picks: number[]): number {
  return picks.reduce((sum, p) => sum + (p > 0 ? p : 0), 0);
}
